import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'

export interface User {
  id: number
  login: string
  courriel?: string | null
  name: string
  role: string
  role_libelle?: string
  initials: string
  groupe?: string
  icon?: string
  code?: string
  grade?: string
  rvh?: string
  lien?: string
  droits?: string
  view?: {
    centerX: number
    centerY: number
    zoom: number
    maxZoom: number
    extentMinX: number
    extentMinY: number
    extentMaxX: number
    extentMaxY: number
  }
  site?: {
    x: number
    y: number
    name: string
  }
  modules?: {
    pc?: boolean
    astreinte?: boolean
    mutualisation_materiel?: boolean
    indicateur?: boolean
    import?: boolean
    formation?: boolean
  }
  module_list?: (number | string)[]
  module_label_list?: string[]
  couche_list?: (number | string)[]
  couche_label_list?: string[]
  default_module_id?: number | null
  default_module_libelle?: string | null
  default_module_lien?: string | null
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('token'))
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!token.value)
  
  const userInitials = computed(() => {
    if (!user.value?.name) return ''
    const parts = user.value.name.trim().split(/\s+/)
    const first = parts[0] || ''
    const last = parts[1] || parts[parts.length - 1] || ''
    return (first.charAt(0) + last.charAt(0)).toUpperCase()
  })

  async function login(login: string, password: string) {
    loading.value = true
    error.value = null

    // Purger toute session résiduelle avant une nouvelle tentative de login
    // (évite qu'un ancien token expiré déclenche des refresh en boucle)
    token.value = null
    user.value = null
    localStorage.removeItem('token')
    
    try {
      const response = await api.post('/auth/login', { login, password })
      token.value = response.data.token
      user.value = response.data.user
      localStorage.setItem('token', response.data.token)
      return true
    } catch (err: any) {
      // Nettoyer tout ancien token pour éviter les boucles de refresh
      token.value = null
      user.value = null
      localStorage.removeItem('token')
      error.value = err.response?.data?.error || 'Erreur de connexion'
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await api.post('/auth/logout')
    } catch {
      // Ignorer les erreurs de logout
    } finally {
      token.value = null
      user.value = null
      localStorage.removeItem('token')
    }
  }

  async function checkAuth() {
    if (!token.value) return false
    
    try {
      const response = await api.get('/auth/me')
      user.value = response.data.user
      return true
    } catch {
      token.value = null
      user.value = null
      localStorage.removeItem('token')
      return false
    }
  }

  async function refreshToken() {
    // Si aucun token n'existe, ne pas appeler l'API (évite les boucles /auth/refresh)
    const existingToken = localStorage.getItem('token')
    if (!existingToken) {
      token.value = null
      user.value = null
      return false
    }

    try {
      const response = await api.post('/auth/refresh')
      token.value = response.data.token
      localStorage.setItem('token', response.data.token)
      return true
    } catch {
      // Ne PAS appeler logout() ici (évite un appel API supplémentaire qui boucle)
      // Nettoyer l'état localement
      token.value = null
      user.value = null
      localStorage.removeItem('token')
      return false
    }
  }

  return {
    token,
    user,
    loading,
    error,
    isAuthenticated,
    userInitials,
    login,
    logout,
    checkAuth,
    refreshToken
  }
})
